// src/components/games/edit/GameEditMediaSlider.js
import React, { useState, useRef, useEffect, useCallback } from 'react';
import { Swiper, SwiperSlide } from 'swiper/react';
import 'swiper/css';
import { FaPlus, FaTrash, FaPlay } from 'react-icons/fa';

/**
 * GameEditMediaSlider
 * - file + preview(blob URL)
 * - edit mode: use URL or generated thumbnail
 */
export default function GameEditMediaSlider({ media = [], onChange }) {
  const [currentIndex, setCurrentIndex] = useState(0);
  const mainSwiperRef = useRef();
  const fileInputRef = useRef();
  const remoteThumbsRef = useRef({});

  // extract a single frame for thumbnail
  const extractFrame = useCallback(source => {
    return new Promise(resolve => {
      const vid = document.createElement('video');
      vid.src = typeof source === 'string' ? source : URL.createObjectURL(source);
      vid.crossOrigin = 'anonymous';
      vid.preload = 'metadata';
      vid.muted = true;
      vid.onloadedmetadata = () => { vid.currentTime = 0.1; };
      vid.onseeked = () => {
        const c = document.createElement('canvas');
        c.width = vid.videoWidth;
        c.height = vid.videoHeight;
        c.getContext('2d').drawImage(vid, 0, 0);
        const dataUrl = c.toDataURL('image/png');
        if (typeof source !== 'string') URL.revokeObjectURL(vid.src);
        resolve(dataUrl);
      };
    });
  }, []);

  // batch extract thumbnails once on media change
  useEffect(() => {
    let cancelled = false;
    const loadThumbs = async () => {
      const newThumbs = {};
      for (let idx = 0; idx < media.length; idx++) {
        const item = media[idx];
        // use existing remoteThumbsRef or inline thumb if provided
        if (item.type === 'video' && !item.thumb && !remoteThumbsRef.current[idx]) {
          try {
            const source = item.preview || item.url;
            newThumbs[idx] = await extractFrame(source);
          } catch {}
        }
      }
      if (!cancelled) {
        remoteThumbsRef.current = { ...remoteThumbsRef.current, ...newThumbs };
      }
    };
    loadThumbs();
    return () => { cancelled = true; };
  }, [media, extractFrame]);

  // add media handler
  const handleAdd = useCallback(async e => {
    if (!e.target.files) return;
    const files = Array.from(e.target.files);
    const newItems = await Promise.all(files.map(async file => {
      const preview = URL.createObjectURL(file);
      if (file.type.startsWith('video')) {
        const thumb = await extractFrame(preview);
        return { type: 'video', file, preview, thumb };
      }
      return { type: 'image', file, preview };
    }));
    onChange(prev => {
      const combined = [...prev, ...newItems];
      // videos first, then images
      const videos = combined.filter(i => i.type === 'video');
      const images = combined.filter(i => i.type !== 'video');
      return [...videos, ...images];
    });
    e.target.value = null;
  }, [onChange, extractFrame]);

  const handleRemove = useCallback(idx => {
    onChange(prev => prev.filter((_, i) => i !== idx));
  }, [onChange]);

  const onSlideChange = swiper => {
    setCurrentIndex(swiper.realIndex);
  };

  return (
    <div className="space-y-4 bg-neutral-800 p-4 rounded">
      {/* add media */}
      <div className="flex gap-2">
        <button
          type="button"
          onClick={() => fileInputRef.current?.click()}
          className="flex items-center px-3 py-1 bg-blue-600 hover:bg-blue-500 rounded text-white text-sm"
        >
          <FaPlus className="mr-1" /> 미디어 추가
        </button>
        <input
          ref={fileInputRef}
          type="file"
          accept="image/*,video/*"
          multiple
          onChange={handleAdd}
          className="hidden"
        />
      </div>

      {/* main swiper */}
      <div className="relative w-full overflow-hidden rounded" style={{ paddingTop: '56.25%' }}>
        <Swiper
          ref={mainSwiperRef}
          onSlideChange={onSlideChange}
          loop={media.length > 2}
          className="absolute inset-0 w-full h-full"
        >
          {media.map((item, idx) => {
            const src = item.preview || item.url;
            return (
              <SwiperSlide key={idx}>
                {item.type === 'video' ? (
                  <video
                    src={src}
                    controls
                    loop
                    className="w-full h-full object-cover"
                  />
                ) : (
                  <img
                    src={src}
                    alt={`media-${idx}`}
                    className="w-full h-full object-cover"
                  />
                )}
                <button
                  type="button"
                  onClick={() => handleRemove(idx)}
                  className="absolute top-2 right-2 bg-black bg-opacity-50 p-1 rounded-full text-white hover:text-red-400"
                >
                  <FaTrash />
                </button>
              </SwiperSlide>
            );
          })}
        </Swiper>
      </div>

      {/* thumbnails */}
      <div className="overflow-x-auto pt-2 pb-4 scrollbar-thin scrollbar-thumb-gray-600 scrollbar-track-neutral-800">
        <div className="flex space-x-2">
          {media.map((item, idx) => {
            const thumbSrc = item.thumb || item.preview || item.url;
            return (
              <div
                key={idx}
                onClick={() => mainSwiperRef.current?.swiper.slideToLoop(idx)}
                className={`flex-shrink-0 w-32 aspect-video rounded overflow-hidden border-2 cursor-pointer ${
                  idx === currentIndex ? 'border-orange-500' : 'border-transparent'
                }`}
              >
                {item.type === 'video' ? (
                  <div className="relative w-full h-full bg-black">
                    <img src={thumbSrc} alt={`thumb-${idx}`} className="w-full h-full object-cover" />
                    <div className="absolute inset-0 flex items-center justify-center bg-black bg-opacity-40">
                      <FaPlay className="text-white text-2xl" />
                    </div>
                  </div>
                ) : (
                  <img src={thumbSrc} alt={`thumb-${idx}`} className="w-full h-full object-cover" />
                )}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
